import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Box, Chip, CircularProgress, Paper, Tooltip} from "@mui/material";
import Utils, {deepEqual, formatMoney} from "../../../../../core/services/utils/utils";
import Form from "../../../base/form";
import SaveOrRollback from "../../save-or-rollback";
import useIsMounted from "../../../../hooks/use-is-mounted";
import {OutlinedInputForm} from "../../../base/input/outlined_input";
import {OutlinedDropdownForm} from "../../../base/dropdown/outlined_dropdown";
import SummaryInfoCard, {summaryInfoCardBodyEntryTypes} from "../../summary-cards/summary-info-card";
import {Col} from "reactstrap";
import {ReactComponent as Passed} from "../../../../../assets/images/validations/passed.svg";
import {ReactComponent as Failed} from "../../../../../assets/images/validations/failed.svg";
import Button from "../../../base/button/button";
import {routeFunctions, routes} from "../../../../../core/models/static/routes/app-routes";
import Api from "../../../../../core/services/api/api";
import {ContentCopy, OpenInNew} from "@mui/icons-material";
import IconButton from "../../../base/button/icon_button";
import ValidateMessages from "../../../../../core/models/static/texts/vallidate-messages";
import ApiResponseCodes from "../../../../../core/models/static/api-response-codes";
import classnames from "classnames";
import DateUtils from "../../../../../core/services/date-utils";
import {MuiRFF, Yup} from '../../../form';
import {useTranslation} from "react-i18next";
import {ShippingBillTo, ShippingBillToList} from "../../../../../core/constants/enums";

import useAccessLevels from "../../../../hooks/use-access-levels";
import {Link} from "react-router-dom";

const formNames = {
    freightCompany: "freightCompany",
    estimatedDate: "estimatedDate",
    estimatedFee: "estimatedFee",
    customerFee: "customerFee",
    trackingNo: "trackingNo",
    shippingBillTo: "shippingBillTo",
    accountNumber: "accountNumber",
    companyName: "companyName",
};

/**
 * Parses freight metaData JSON from the server into form fields.
 * @param {string|null|undefined} metaData
 * @returns {{accountNumber: string|null, companyName: string|null}}
 */
export const parseFreightMetaData = (metaData) => {
    if (!metaData || typeof metaData !== 'string') {
        return {accountNumber: null, companyName: null};
    }
    try {
        const parsed = JSON.parse(metaData);
        return {
            accountNumber: parsed?.accountNumber ?? null,
            companyName: parsed?.companyName ?? null,
        };
    } catch {
        return {accountNumber: null, companyName: null};
    }
};

/**
 * Builds the stringified freight metaData payload for save APIs.
 * Only one of accountNumber / companyName is set based on shippingBillTo.
 * @param {string|null|undefined} shippingBillTo
 * @param {string|null|undefined} accountNumber
 * @param {string|null|undefined} companyName
 * @returns {string}
 */
export const stringifyFreightMetaData = (shippingBillTo, accountNumber, companyName) => {
    if (shippingBillTo === ShippingBillTo.collect) {
        return JSON.stringify({accountNumber: accountNumber || null, companyName: null});
    }
    if (shippingBillTo === ShippingBillTo.thirdParty) {
        return JSON.stringify({accountNumber: null, companyName: companyName || null});
    }
    return JSON.stringify({accountNumber: null, companyName: null});
};

/**
 * Whether the selected freight company requires shipping bill-to fields.
 * Hidden when the company has noCarrierFee (without carried fees).
 * @param {{noCarrierFee?: boolean}|null|undefined} freightCompany
 * @returns {boolean}
 */
const showsShippingBillTo = (freightCompany) =>
    !!freightCompany && freightCompany.id !== -1 && freightCompany.noCarrierFee !== true;

const schema = Yup.object().shape({
    [formNames.freightCompany]: Yup.mixed().nullable().required(ValidateMessages.required),
    [formNames.estimatedDate]: Yup.mixed().nullable().required(ValidateMessages.required),
    [formNames.estimatedFee]: Yup.number().nullable().required(ValidateMessages.required).typeError(ValidateMessages.incorrectType('number')),
    [formNames.customerFee]: Yup.number().nullable().required(ValidateMessages.required).typeError(ValidateMessages.incorrectType('number')),
    [formNames.trackingNo]: Yup.string().nullable(),
    [formNames.shippingBillTo]: Yup.string().nullable().when(formNames.freightCompany, {
        is: (freightCompany) => showsShippingBillTo(freightCompany),
        then: Yup.string().nullable().required(ValidateMessages.required),
        otherwise: Yup.string().nullable(),
    }),
    [formNames.accountNumber]: Yup.string().nullable().when(formNames.shippingBillTo, {
        is: ShippingBillTo.collect,
        then: Yup.string().nullable(),
        otherwise: Yup.string().nullable(),
    }),
    [formNames.companyName]: Yup.string().nullable().when(formNames.shippingBillTo, {
        is: ShippingBillTo.thirdParty,
        then: Yup.string().nullable(),
        otherwise: Yup.string().nullable(),
    }),
})

const required = MuiRFF.makeRequired(schema);
const validate = MuiRFF.makeValidate(schema);


const summaryInfoBodyIds = {
    freightCompany: 'freightCompany',
    estimatedDate: 'estimatedTime',
    estimatedFee: 'estimatedFee',
    customerFee: 'customerFee',
    trackingNo: 'trackingNo',
    shippingBillTo: 'shippingBillTo',
    accountNumber: 'accountNumber',
    companyName: 'companyName',
    eligibleForFreeShipping: "eligibleForFreeShipping",
}

const OrderInformationViewShippingInfoCard = ({
                                                  shippingInfo = {},
                                                  update,
                                                  updateTrackingNo,
                                                  freeShippingEligibility,
                                                  getFreeShippingEligibility,
                                                  getFreightEstimation,
                                                  freightEstimation,
                                                  loadingFreightEstimation = false,
                                                  viewOnly = false,
                                                  loading = false,
                                                  customerId,
                                                  onFreightCompanyChanged: onFreightCompanyChangedProps,
                                              }) => {
    const {t} = useTranslation('components', {keyPrefix: 'app-specific.order-information.shipping-info-card'});
    const [submitting, setSubmitting] = useState(false);
    const [shippingCompanies, setShippingCompanies] = useState([]);
    const [customerDetached, setCustomerDetached] = useState(false);
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();

    const parsedMetaData = useMemo(
        () => parseFreightMetaData(shippingInfo?.metaData),
        [shippingInfo?.metaData],
    );

    /**
     * Form initial values from saved shipping info.
     * Depends only on form-relevant fields so incidental shippingInfo patches
     * (e.g. freeFreightId from eligibility) do not recreate initialValues and
     * cause React Final Form to reinitialize / wipe dirty company selection.
     */
    const initialValues = useMemo(() => {
        return ({
            [formNames.freightCompany]: shippingCompanies?.find(e => e.id !== -1 && ((shippingInfo?.freightCompany?.id && e.id === shippingInfo?.freightCompany?.id) || (shippingInfo?.freightCompany?.code && e.code === shippingInfo?.freightCompany?.code))) ?? null,
            [formNames.estimatedDate]: shippingInfo?.estimatedDate ? DateUtils.serverToUTC(shippingInfo?.estimatedDate) : null,
            [formNames.trackingNo]: shippingInfo?.trackingNo,
            [formNames.shippingBillTo]: shippingInfo?.shippingBillTo ?? null,
            [formNames.accountNumber]: parsedMetaData.accountNumber,
            [formNames.companyName]: parsedMetaData.companyName,
            ...(!viewOnly && {
                [formNames.estimatedFee]: shippingInfo?.estimatedFee ?? 0,
                [formNames.customerFee]: shippingInfo?.customerFee ?? 0,
            }),
            ...(viewOnly && {
                [formNames.estimatedFee]: shippingInfo?.estimatedFee,
                [formNames.customerFee]: shippingInfo?.customerFee,
            }),

        })
    }, [
        shippingInfo?.freightCompany?.id,
        shippingInfo?.freightCompany?.code,
        shippingInfo?.estimatedDate,
        shippingInfo?.estimatedFee,
        shippingInfo?.customerFee,
        shippingInfo?.trackingNo,
        shippingInfo?.shippingBillTo,
        shippingCompanies,
        viewOnly,
        parsedMetaData,
    ])

    const canShowFreeShipping = !!(
        operatorAccessLevels.packages.sales.orderFreightInfo
        || operatorAccessLevels.packages.sales.quoteFreightInfo
    );

    const freeShippingInfo = useMemo(() => ({
        exists: !!freeShippingEligibility?.id && canShowFreeShipping,
        icon: <span style={{minWidth: '1rem', minHeight: '1rem'}}>
            {
                freeShippingEligibility?.isFree
                    ? <Passed className={'icon normal'}/>
                    : <Failed className={'icon normal'}/>
            }
        </span>,
        className: freeShippingEligibility?.isFree ? 'free-shipping-eligible' : 'free-shipping-not-eligible',
        title: (customerDetached ? "(Detached Customer) " : "")
            + (freeShippingEligibility?.isFree ? 'Eligible for free shipping' : 'Not eligible for free shipping'),
        message: freeShippingEligibility?.friendlyReason?.split('\n')?.filter(e => !!e) ?? [],
    }), [freeShippingEligibility?.id, freeShippingEligibility?.isFree, freeShippingEligibility?.friendlyReason, canShowFreeShipping, customerDetached])

    const estimatedFreightAmount = freightEstimation?.amount;

    const shippingBillToOptions = useMemo(() => (
        ShippingBillToList.map((key) => ({
            value: key,
            label: t(`billToOptions.${key}`),
        }))
    ), [t]);

    /**
     * Renders the estimated freight chip and optional copy action.
     *
     * @param {import('final-form').FormApi} [form]
     * @returns {React.ReactNode}
     */
    const renderEstimatedFreightAdornment = useCallback((form) => {
        if (estimatedFreightAmount == null && !loadingFreightEstimation)
            return null;
        return (
            <Box className={'d-flex align-items-center'} sx={{gap: 0.5}}>
                {
                    loadingFreightEstimation
                        ? <CircularProgress size={16}/>
                        : (
                            <>
                                <Chip
                                    size={'medium'}
                                    label={t('estimatedFreight', {amount: formatMoney(estimatedFreightAmount)})}
                                    color={'default'}
                                    variant={'outlined'}
                                    sx={{
                                        minHeight: 40
                                    }}
                                    onDelete={() => void 0}
                                    deleteIcon={
                                        !viewOnly && form &&
                                        <Tooltip title={t('copyEstimatedFreight')}>
                                            <Box component={'span'} sx={{display: 'block'}}>
                                                <IconButton
                                                    disabled={loading || loadingFreightEstimation}
                                                    color={'paper.secondary'}
                                                    onPressed={() => {
                                                        form.change(formNames.estimatedFee, estimatedFreightAmount)
                                                        // focus the input
                                                        setTimeout(() => {
                                                            const input = document.querySelector(`input[name="${formNames.estimatedFee}"]`);
                                                            if (input) {
                                                                input.focus();
                                                                input.select();
                                                            }
                                                        }, 100);
                                                    }}
                                                >
                                                    <ContentCopy className={'icon small'}/>
                                                </IconButton>
                                            </Box>
                                        </Tooltip>
                                    }
                                />
                            </>
                        )
                }
            </Box>
        );
    }, [estimatedFreightAmount, loading, loadingFreightEstimation, t, viewOnly]);

    /**
     * Determines if the selected customer is detached or not.
     * @type {(function(*): Promise<undefined|void>)|*}
     */
    const determineIfSelectedCustomerDetached = useCallback(async (selectedCustomerId) => {
        if (!selectedCustomerId)
            return;
        const response = await Api.getFreightCustomerExceptionInfo(selectedCustomerId);
        if (!isMounted())
            return;
        if (response.errorCode === ApiResponseCodes.freeFreightCustomerNotDetached) {
            return setCustomerDetached(false);
        } else {
            setCustomerDetached(true);
        }
    }, [isMounted])

    /**
     * With the selected customer id, determines if the customer is detached or not.
     */
    useEffect(() => {
        if (!customerId) {
            return setCustomerDetached(false);
        }
        determineIfSelectedCustomerDetached(customerId).then();
    }, [customerId, determineIfSelectedCustomerDetached])

    /**
     * Updates the summary info values of the summary card by invoking their specific callbacks.
     * @type {(function(*, *): void)|*}
     */
    const updateSummaryInfo = useCallback(async (entry, newValue) => {
        switch (entry.id) {
            case summaryInfoBodyIds.trackingNo: {
                return updateTrackingNo?.(newValue);
            }
            default:
                return;
        }
    }, [updateTrackingNo]);

    const summaryShowsBillTo = showsShippingBillTo(initialValues?.freightCompany)
        || (!initialValues?.freightCompany && !!initialValues?.shippingBillTo);

    const summaryInfo = useMemo(() => ({
        header: {
            title: t('title'),
            value: '',
            loadingValue: "",
        },
        body: [
            {
                id: summaryInfoBodyIds.trackingNo,
                title: t('trackingNo'),
                value: initialValues?.trackingNo,
                editable: !!updateTrackingNo && !!shippingInfo?.id,
                clearable: !!updateTrackingNo && !!shippingInfo?.id,
                edit: !!updateTrackingNo ? updateSummaryInfo : undefined,
            },
            {
                id: summaryInfoBodyIds.freightCompany,
                title: t('freightCompany'),
                value: initialValues?.freightCompany?.title,
            },
            ...(summaryShowsBillTo ? [
                {
                    id: summaryInfoBodyIds.shippingBillTo,
                    title: t('shippingBillTo'),
                    value: initialValues?.shippingBillTo
                        ? t(`billToOptions.${initialValues.shippingBillTo}`)
                        : null,
                },
                ...(initialValues?.shippingBillTo === ShippingBillTo.collect ? [{
                    id: summaryInfoBodyIds.accountNumber,
                    title: t('accountNumber'),
                    value: initialValues?.accountNumber,
                }] : []),
                ...(initialValues?.shippingBillTo === ShippingBillTo.thirdParty ? [{
                    id: summaryInfoBodyIds.companyName,
                    title: t('companyName'),
                    value: initialValues?.companyName,
                }] : []),
            ] : []),
            {
                id: summaryInfoBodyIds.estimatedDate,
                title: t('shippingDate'),
                value: initialValues?.estimatedDate,
                format: (val) => val ? DateUtils.serverToLocal(val).format(DateUtils.dateFormat) : '--',
                type: summaryInfoCardBodyEntryTypes.date,
            },
            {
                id: summaryInfoBodyIds.estimatedFee,
                title: t('freightQuote'),
                value: initialValues?.estimatedFee,
                format: formatMoney,
            },
            {
                id: summaryInfoBodyIds.customerFee,
                title: t('customerCharges'),
                value: initialValues?.customerFee,
                format: formatMoney,
            },
            ...(freeShippingInfo?.exists ?
                    [{
                        id: summaryInfoBodyIds.eligibleForFreeShipping,
                        className: freeShippingInfo.className,
                        titlePrefix: freeShippingInfo.icon,
                        title: <>
                            <div className={'d-flex align-items-start'}>
                                {freeShippingInfo.title}:
                                {
                                    !!freeShippingInfo.message?.length &&
                                    <IconButton
                                        disabled={loading}
                                        className={"mx-1 ml-2"}
                                        color={'paper.secondary'}
                                        component={Link}
                                        target={'_blank'}
                                        to={
                                            customerDetached
                                                ? routeFunctions.customer.shippingFees.settings(customerId)
                                                : routes.shippingFees.settings
                                        }
                                    >
                                        {
                                            loading
                                                ? <CircularProgress size={15} color={'primary'}/>
                                                : <OpenInNew className={"icon small"}/>
                                        }
                                    </IconButton>
                                }
                            </div>
                            {
                                !!freeShippingInfo.message.length &&
                                <ul style={{marginLeft: '-2rem'}}>
                                    {
                                        freeShippingInfo.message?.map(e => (
                                            <li key={e}>
                                                <span className={'text-sm font-weight-normal'}>{e}</span>
                                                <br/>
                                            </li>
                                        ))
                                    }
                                </ul>
                            }
                        </>,
                        hideValue: true,
                    }]
                    : []
            )
        ]
    }), [initialValues?.trackingNo, initialValues?.freightCompany?.title, initialValues?.shippingBillTo, initialValues?.accountNumber, initialValues?.companyName, initialValues?.estimatedDate, initialValues?.estimatedFee, initialValues?.customerFee, updateTrackingNo, shippingInfo?.id, updateSummaryInfo, freeShippingInfo?.exists, freeShippingInfo.className, freeShippingInfo.icon, freeShippingInfo.title, freeShippingInfo.message, loading, customerDetached, customerId, summaryShowsBillTo, t])

    /**
     * Fetches the shipping companies of the system.
     * @returns {Promise<void>}
     */
    const getShippingCompanies = useCallback(async () => {
        const response = await Api.getFreightCompanies({});
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            const res = [
                ...(response?.data ?? []),
                {id: -1}
            ]
            setShippingCompanies(res);
        }
    }, [isMounted])

    /**
     * With each change in the view only prop:
     * - fetches the shipping companies of the system.
     */
    useEffect(() => {
        void getShippingCompanies();
    }, [getShippingCompanies]);

    /**
     * Reverts the changes made in the form values.
     * @param form
     */
    const revertValues = (form) => {
        form.getRegisteredFields().forEach((name) => {
            form.change(name, initialValues[name]);
            form.resetFieldState(name);
        })
    }

    /**
     * Navigates the user to the freight-companies list view.
     * @type {(function(*): void)}
     */
    const navigateToCompaniesListView = useCallback((e) => {
        e.stopPropagation();
        e.preventDefault();
        window.open(window.location.origin + routes.shippingFees.companies, '_blank')
    }, [])


    /**
     * Validates the form values and then submits the form.
     * Clears bill-to/metadata when the company has noCarrierFee.
     * @type {(function(*, *): Promise<void>)|*}
     */
    const submitForm = useCallback(async (values, form) => {
        setSubmitting(true);
        if (update) {
            const hideBillTo = values?.freightCompany?.noCarrierFee === true;
            const shippingBillTo = hideBillTo ? null : (values?.shippingBillTo ?? null);
            await update({
                ...values,
                estimatedDate: DateUtils.UTCToServer(values.estimatedDate).format(DateUtils.withoutOffsetFormat),
                shippingBillTo,
                metaData: hideBillTo
                    ? stringifyFreightMetaData(null, null, null)
                    : stringifyFreightMetaData(shippingBillTo, values?.accountNumber, values?.companyName),
            }, form)
        }
        setSubmitting(false);
    }, [update]);

    /**
     * Changes the value of the freight company field and then fetches the free shipping eligibility of the selected freight company.
     * Clears bill-to and metadata when switching to a noCarrierFee company.
     * @type {(function(*, formApi): void)}
     */
    const onFreightCompanyChanged = useCallback((e, form) => {
        const freightCompany = e.target.value;
        form.change(e.target.name, freightCompany);
        if (freightCompany?.noCarrierFee === true) {
            form.change(formNames.shippingBillTo, null);
            form.change(formNames.accountNumber, null);
            form.change(formNames.companyName, null);
        }
        if (freightCompany?.id !== -1) {
            if (onFreightCompanyChangedProps)
                onFreightCompanyChangedProps(freightCompany);
            if (getFreeShippingEligibility)
                getFreeShippingEligibility(freightCompany?.id).then();
            if (getFreightEstimation)
                getFreightEstimation(freightCompany?.id).then();
        }
    }, [getFreeShippingEligibility, getFreightEstimation, onFreightCompanyChangedProps])

    /**
     * Clears the unused metadata field when shipping bill-to changes.
     * @type {(function(*, formApi): void)}
     */
    const onShippingBillToChanged = useCallback((e, form) => {
        const shippingBillTo = e.target.value;
        form.change(formNames.shippingBillTo, shippingBillTo);
        if (shippingBillTo === ShippingBillTo.collect) {
            form.change(formNames.companyName, null);
        } else if (shippingBillTo === ShippingBillTo.thirdParty) {
            form.change(formNames.accountNumber, null);
        } else {
            form.change(formNames.accountNumber, null);
            form.change(formNames.companyName, null);
        }
    }, [])


    return (
        <div className={'order-shipping-info-card'}>
            {
                viewOnly &&
                <>
                    {
                        (estimatedFreightAmount != null || loadingFreightEstimation) &&
                        <div className={'d-flex justify-content-end mb-2'}>
                            {renderEstimatedFreightAdornment()}
                        </div>
                    }
                    <SummaryInfoCard
                        header={summaryInfo.header}
                        body={summaryInfo.body}
                        loading={loading}
                        loadingCount={summaryInfo.body.length}
                    />
                </>
            }
            {
                !viewOnly &&
                <Paper className='paper background-white p-3'>
                    <Form
                        className={'w-100'}
                        onSubmit={submitForm}
                        validate={validate}
                        initialValues={initialValues}
                        render={({values, initialValues, form}) => {
                            const changed = !deepEqual(
                                Utils.excludeNullOrUndefined(values, false),
                                Utils.excludeNullOrUndefined(initialValues, false)
                            );
                            const showBillTo = showsShippingBillTo(values?.freightCompany);
                            return (
                                <>
                                    <div className={'d-flex flex-column mb-3'}>
                                        <div className={'d-flex align-items-center justify-content-between mb-4'}>
                                            <p className='black text-md font-weight-semi-bold'>
                                                {t('title')}
                                            </p>
                                            <div className={'d-flex align-items-center flex-wrap justify-content-end'} style={{gap: '0.75rem'}}>
                                                <SaveOrRollback
                                                    column={false}
                                                    show={changed}
                                                    loading={submitting}
                                                    onSave={() => form.submit()}
                                                    onRollback={() => revertValues(form)}
                                                />
                                                {renderEstimatedFreightAdornment(form)}
                                            </div>
                                        </div>
                                        {
                                            loading
                                                ? <div className='w-100 d-flex flex-column loading customer-inner'>
                                                    <div/>
                                                    <div/>
                                                    <div/>
                                                    <div/>
                                                    <div/>
                                                    <div/>
                                                </div>
                                                : <div className={'d-flex flex-wrap'}>
                                                    <Col xs={12} xl={6}>
                                                        <OutlinedDropdownForm
                                                            className={'mb-3'}
                                                            name={formNames.freightCompany}
                                                            label={t('freightCompany')}
                                                            required={!!required[formNames.freightCompany]}
                                                            renderValue={v => v?.label ?? v?.title ?? ''}
                                                            renderOptions={(option) => {
                                                                if (option?.id === -1) {
                                                                    return (
                                                                        <Button
                                                                            sx={{marginInline: 'auto'}}
                                                                            variant={'contained'}
                                                                            onClick={navigateToCompaniesListView}
                                                                        >
                                                                            {t('manageCompanies')}
                                                                        </Button>
                                                                    )
                                                                }
                                                                return option?.label ?? '';
                                                            }}
                                                            data={shippingCompanies?.map(e => ({
                                                                value: e,
                                                                label: e.title,
                                                                id: e.id,
                                                            }))}
                                                            onChange={(e) => onFreightCompanyChanged(e, form)}
                                                        />
                                                    </Col>
                                                    {
                                                        showBillTo &&
                                                        <Col xs={12} xl={6}>
                                                            <OutlinedDropdownForm
                                                                className={'mb-3'}
                                                                name={formNames.shippingBillTo}
                                                                label={t('shippingBillTo')}
                                                                required
                                                                renderValue={v => shippingBillToOptions.find(o => o.value === v)?.label ?? ''}
                                                                data={shippingBillToOptions}
                                                                onChange={(e) => onShippingBillToChanged(e, form)}
                                                            />
                                                        </Col>
                                                    }
                                                    {
                                                        showBillTo && values?.shippingBillTo === ShippingBillTo.collect &&
                                                        <Col xs={12} xl={6}>
                                                            <OutlinedInputForm
                                                                label={t('accountNumber')}
                                                                className={'mb-3'}
                                                                name={formNames.accountNumber}
                                                                type={'text'}
                                                                required
                                                            />
                                                        </Col>
                                                    }
                                                    {
                                                        showBillTo && values?.shippingBillTo === ShippingBillTo.thirdParty &&
                                                        <Col xs={12} xl={6}>
                                                            <OutlinedInputForm
                                                                label={t('companyName')}
                                                                className={'mb-3'}
                                                                name={formNames.companyName}
                                                                type={'text'}
                                                                required
                                                            />
                                                        </Col>
                                                    }
                                                    <Col xs={12} xl={6}>
                                                        <MuiRFF.DatePicker
                                                            className={'mb-3'}
                                                            name={formNames.estimatedDate}
                                                            label={t('shippingDate')}
                                                            required={required[formNames.estimatedDate]}
                                                        />
                                                    </Col>
                                                    <Col xs={12} xl={6}>
                                                        <OutlinedInputForm
                                                            label={t('freightQuote')}
                                                            className={'mb-3'}
                                                            name={formNames.estimatedFee}
                                                            type={'number'}
                                                            required={required[formNames.estimatedFee]}
                                                        />
                                                    </Col>
                                                    <Col xs={12} xl={6}>
                                                        <OutlinedInputForm
                                                            label={t('customerCharges')}
                                                            className={'mb-3'}
                                                            name={formNames.customerFee}
                                                            type={'number'}
                                                            required={required[formNames.customerFee]}
                                                        />
                                                    </Col>
                                                    <Col xs={12} xl={6}>
                                                        <OutlinedInputForm
                                                            label={t('trackingNo')}
                                                            className={'mb-3'}
                                                            name={formNames.trackingNo}
                                                            type={'text'}
                                                            required={required[formNames.trackingNo]}
                                                        />
                                                    </Col>
                                                    {
                                                        freeShippingInfo?.exists &&
                                                        <Col xs={12} className={'d-flex flex-column'}>
                                                            <div className={'d-flex align-items-start'}>
                                                                {freeShippingInfo.icon}
                                                                <p className={freeShippingInfo.className}>
                                                                    {freeShippingInfo.title}
                                                                </p>
                                                                {
                                                                    !!freeShippingInfo.message?.length &&
                                                                    <IconButton
                                                                        disabled={loading}
                                                                        className={"mx-1 ml-2"}
                                                                        color={'paper.secondary'}
                                                                        component={Link}
                                                                        target={'_blank'}
                                                                        to={
                                                                            customerDetached
                                                                                ? routeFunctions.customer.shippingFees.settings(customerId)
                                                                                : routes.shippingFees.settings
                                                                        }
                                                                    >
                                                                        {
                                                                            loading
                                                                                ? <CircularProgress size={15} color={'primary'}/>
                                                                                : <OpenInNew className={"icon small"}/>
                                                                        }
                                                                    </IconButton>
                                                                }
                                                            </div>
                                                            <ul style={{marginLeft: '-1rem'}}
                                                                className={classnames(freeShippingInfo.className, 'text-sm mt-2')}>
                                                                {
                                                                    freeShippingInfo.message?.map(e => (
                                                                        <li key={e}>
                                                                            {e}
                                                                            <br/>
                                                                        </li>
                                                                    ))
                                                                }
                                                            </ul>
                                                        </Col>
                                                    }
                                                </div>
                                        }
                                    </div>
                                </>
                            );
                        }}
                    />
                </Paper>
            }
        </div>
    )
}

export default OrderInformationViewShippingInfoCard;
